import {Trans} from '@lingui/macro';
import {flow} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserTheme, useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {MarkdownPlainFormatter} from '../../../../../../srcCommon/formatters/markdown/markdownPlainFormatter';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {EmojiMap} from '../../../../../services/emojis/emojis';
import {getEmojiValidationMap} from '../../../../../store/stateless/emojiSelectors';
import {TeammateAvatar} from '../../../../common/avatars/teammateAvatar';
import {makeMarkdownTransformEmojis} from '../../../../common/formatters/markdown/markdownTransformEmojis';
import {makeMarkdownTransformMentions} from '../../../../common/formatters/markdown/markdownTransformMentions';
import {NotificationCellBase, NotificationCellBasePublicProps} from './notificationCellBase';

/*
 * Props.
 */

interface MentionNotificationCellProps extends NotificationCellBasePublicProps {
  userAlias: string;
}

/*
 * Style.
 */

const StyledNameSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const MentionNotificationCell: FC<MentionNotificationCellProps> = (props) => {
  const theme = useUserTheme();
  return (
    <NotificationCellBase {...props} icon={renderIcon(props)} title={renderTitle(props)}>
      {renderSummary(props, useAppSelector(getEmojiValidationMap), theme)}
    </NotificationCellBase>
  );
};

function renderIcon(props: MentionNotificationCellProps) {
  const {fromTeammate} = props.notification;
  if (!fromTeammate) {
    return null;
  }

  return <TeammateAvatar teammate={fromTeammate} size={VisualSizesEnum.SMALL} />;
}

function renderTitle(props: MentionNotificationCellProps) {
  const {fromTeammate} = props.notification;
  if (!fromTeammate) {
    return <Trans id="nc-mention-generic">You were mentioned</Trans>;
  }

  return (
    <Trans id="nc-mention-teammate">
      <StyledNameSpan>{buildTeammateDisplayName(fromTeammate)}</StyledNameSpan> mentioned you
    </Trans>
  );
}

function renderSummary(props: MentionNotificationCellProps, validationMap: EmojiMap, theme: UserTheme) {
  const markdownTransforms = makeMarkdownTransforms(
    props.userAlias,
    validationMap,
    theme.palette.blue.shade40,
  );
  return <MarkdownPlainFormatter text={props.notification.summary} transform={markdownTransforms} />;
}

/*
 * Transform Mentions and Emojis in the text.
 */

const makeMarkdownTransforms = memoize(2)(
  (userAlias: string, validationMap: EmojiMap, color?: string) => (node: ReactNode) =>
    flow(
      makeMarkdownTransformMentions(new Set([userAlias, 'all']), color),
      makeMarkdownTransformEmojis(validationMap),
    )(node),
);
